import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useHistory } from 'react-router-dom';
import _t from 'counterpart';
import { CRow, CCol, CCard, CCardHeader, CCardBody, CButton, CBadge } from '@coreui/react';
import PageLayout from '../../../components/PageLayout';
import Loading from '../../../components/Loading';
import { getDiscountCode, deleteDiscountCode, getPropFunnels } from '../../../services/BackendService';
import { IDiscountCode } from './types';
import { extractErrorMessage } from '../../../helpers';
import { useAppSelector } from '../../../helpers/customHooks';
import { Permission } from '../../../reducers/userReducer';
import ConfirmationModal from '../../../components/ConfirmationModal';
import Detail from '../../../components/micro/Detail';
import toast from 'react-hot-toast';

const DiscountCodePage = () => {
  const [deleteModalShow, setDeleteModalShow] = useState<boolean>(false);

  const permissions = useAppSelector((state) => state.user.permissions);
  const params = useParams();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { id } = params as any;

  const { data: discountCode, isLoading } = useQuery<IDiscountCode>(['discount-code', id], () => getDiscountCode(id), {
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
    retry: false,
  });

  const { data: allFunnels } = useQuery('prop-funnels', getPropFunnels, {
    onError: (e: any) => {
      // Silently handle error for funnel count
    },
  });

  const deleteDiscountCodeMutation = useMutation((id: string) => deleteDiscountCode(id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('discount-codes');
      toast.success(_t('discount-codes.discount-code-deleted-successfully'));
      setDeleteModalShow(false);
      history.push('/discount-codes');
    },
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
    retry: false,
  });

  const onEditClicked = () => {
    history.push(`/discount-codes/form?edit=${id}`);
  };

  const onDeleteClicked = () => {
    setDeleteModalShow(true);
  };

  const deleteConfirmed = () => {
    deleteDiscountCodeMutation.mutate(id);
  };

  const formatDiscountValue = (discountCode: IDiscountCode) => {
    if (discountCode.fixedDiscountAmount) {
      const amount = Number(discountCode.fixedDiscountAmount);
      return `$${amount.toFixed(2)}`;
    } else if (discountCode.percentageDiscount) {
      const percentage = Number(discountCode.percentageDiscount);
      return `${percentage}%`;
    }
    return '-';
  };

  const formatDate = (date: Date | null) => {
    if (!date) return _t('discount-codes.unlimited');
    return new Date(date).toLocaleDateString();
  };

  const hasPermission = (permission: Permission) => {
    return permissions?.includes(permission);
  };

  if (isLoading || !discountCode) {
    return <Loading />;
  }

  return (
    <PageLayout
      title={discountCode.name}
      titleAppend={
        <>
          {hasPermission(Permission.MANAGE_PROP_FUNNELS) && (
            <div className="d-flex justify-content-between float-right mb-2">
              <div className="d-flex flex-nowrap flex-row-reverse">
                <CButton color="primary" onClick={onEditClicked}>
                  {_t('action.edit')}
                </CButton>
                <CButton color="danger" className="mr-2" onClick={onDeleteClicked}>
                  {_t('action.delete')}
                </CButton>
              </div>
            </div>
          )}
        </>
      }
    >
      <CRow>
        <CCol md={12}>
          <CCard>
            <CCardBody>
              <CRow>
                <CCol md={6}>
                  <dl>
                    <Detail title={_t('global.name')}>{discountCode.name}</Detail>
                    <Detail title={_t('discount-codes.code')}>
                      <code>{discountCode.code}</code>
                    </Detail>
                    <Detail title={_t('discount-codes.discount')}>{formatDiscountValue(discountCode)}</Detail>
                    <Detail title={_t('discount-codes.valid-from')}>{formatDate(discountCode.startDate)}</Detail>
                    <Detail title={_t('discount-codes.valid-until')}>{formatDate(discountCode.endDate)}</Detail>
                  </dl>
                </CCol>
                <CCol md={6}>
                  <dl>
                    <Detail title={_t('discount-codes.max-uses-per-user')}>
                      {discountCode.maxUsesPerUser || _t('discount-codes.unlimited')}
                    </Detail>
                    <Detail title={_t('global.status')}>
                      <CBadge color={discountCode.isActive ? 'success' : 'danger'}>
                        {discountCode.isActive ? _t('global.active') : _t('global.inactive')}
                      </CBadge>
                    </Detail>
                    <Detail title={_t('discount-codes.applicable-funnels')}>
                      {discountCode.funnels.length === 0 ||
                      (allFunnels && discountCode.funnels.length === allFunnels.length) ? (
                        _t('discount-codes.all-funnels')
                      ) : (
                        <ul className="list-unstyled mb-0">
                          {discountCode.funnels.map((funnel) => (
                            <li key={funnel.id}>{funnel.funnelId}</li>
                          ))}
                        </ul>
                      )}
                    </Detail>
                  </dl>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <ConfirmationModal
        id="delete-discount-code"
        show={deleteModalShow}
        hidePanel={() => setDeleteModalShow(false)}
        onConfirm={() => deleteConfirmed()}
        title={_t('discount-codes.delete-modal-title')}
        error={null}
        buttonColor="danger"
        withMutation={false}
      />
    </PageLayout>
  );
};

export default DiscountCodePage;
